import { Box, Flex } from 'reflexbox';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import MultilingualQuill from 'packages/kakapo-ui-kit/components/forms/controls/MultilingualQuill';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import { style as fieldStyle } from 'packages/kakapo-ui-kit/components/forms/controls/Field';

import MomentTemplateEditorTypeSettings from './MomentTemplateEditorTypeSettings';

const Label = styled(Box)`
	font-size: 16px;
	color: ${(props) => props.theme.colors.lightBlueDark};
`;

const Cell = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
`;

export const InputElement = styled.input`
	${fieldStyle}
`;

function Notification({
	name,
	url,
	onRemove,
	momentDuration,
}: {
	name: string;
	url: string;
	onRemove: () => void;
	momentDuration: string;
}) {
	const { t } = useTranslation();

	const [{ value: offset }, , { setValue: setOffset }] = useField<number>(`${name}.offset`);
	const [{ value: duration }] = useField<number>(momentDuration);
	const [{ value: type }, , { setValue: setType }] = useField<graphql.MomentNotificationType>(
		`${name}.type`,
	);

	const limitOffset = (offsetType: graphql.MomentNotificationType, offsetNumber: number) => {
		let max = 365;

		if (offsetType === graphql.MomentNotificationType.BeforeEnd) {
			max = duration - 2;
		}

		setOffset(Math.max(0, Math.min(max, offsetNumber)));
	};

	return (
		<Flex alignItems="center" pt={2}>
			<Cell mr={3}>
				<InputElement
					type="number"
					min={0}
					max={365}
					step={1}
					small
					value={offset}
					onChange={(event) => {
						limitOffset(type, parseInt(event.target.value, 10));
					}}
				/>
			</Cell>
			<Cell mr={5}>{t('common.settings.momentTemplates.edit.reminders.days')}</Cell>
			<Cell mr={3} width={6 / 12}>
				<Select
					small
					unspaced
					value={{
						label: t(`common.settings.momentTemplates.edit.reminders.${type.toLowerCase()}`),
						value: type,
					}}
					options={Object.values(graphql.MomentNotificationType).map((notificationType) => ({
						label: t(
							`common.settings.momentTemplates.edit.reminders.${notificationType.toLowerCase()}`,
						),
						value: notificationType,
					}))}
					onChange={(option) => {
						if (option) {
							setType(option.value);
							limitOffset(option.value, offset);
						}
					}}
				/>
			</Cell>
			<DeleteButton onClick={onRemove} />
		</Flex>
	);
}

const Hint = styled(Flex)`
	background: ${(props) => props.theme.colors.whiteBlue};
	padding: 10px;
	border-radius: 4px;
	margin-top: 15px;
	align-items: center;
`;

const HintIcon = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	flex-shrink: 0;
	flex-grow: 0;
	text-transform: uppercase;
	margin-right: 10px;
	font-weight: bold;
	font-size: 12px;
`;

const HintMessage = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	flex-shrink: 1;
	flex-grow: 1;
`;

export default function MomentTemplateEditor({
	url,
	returnURL,
	name,
}: {
	url: string;
	returnURL: string;
	name: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	const [notifications, , { setValue: setNotificationsValue }] = useField<
		graphql.MomentNotification[]
	>(`${name}.notifications`);

	const [{ value: momentTemplate }, ,] = useField<graphql.MomentTemplateInput>(name);

	const error = undefined;

	const showDuration = momentTemplate.type !== graphql.MomentType.Message;

	return (
		<Flex flexDirection="column" mt={3}>
			<Box mb={3}>
				<Box mb={2}>
					<SubSectionTitle>{t('common.settings.momentTemplates.edit.details')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<GraphQLErrorMessage error={error} showValidationDetails />
			<DetailsTableForm
				formValues={[
					{
						label: t('common.settings.momentTemplates.edit.name'),
						name: `${name}.title`,
						type: DetailsTableFormValueTypes.Multilingual,
					},
					{
						label: t('common.settings.momentTemplates.edit.when'),
						name: `${name}.startOffset`,
						type: DetailsTableFormValueTypes.Number,
					},
					...(showDuration
						? [
								{
									label: t('common.settings.momentTemplates.edit.duration'),
									name: `${name}.duration`,
									type: DetailsTableFormValueTypes.Number,
								},
						  ]
						: []),
				]}
			/>

			<MomentTemplateEditorTypeSettings
				name={name}
				id={momentTemplate.id!}
				type={momentTemplate.type}
			/>

			{momentTemplate.type !== graphql.MomentType.Message && (
				<>
					<Label mt={5} mb={1}>
						{t('common.settings.momentTemplates.edit.guide')}
					</Label>
					<Box mb={3}>
						<ParagraphSmall color="slateBlue">
							{t('common.settings.momentTemplates.edit.guideDescription')}
						</ParagraphSmall>
					</Box>
					<MultilingualQuill name={`${name}.description`} />
				</>
			)}

			<Box mt={3}>
				<Label mt={5} mb={1}>
					{t('common.settings.momentTemplates.edit.instructionLabel')}
				</Label>
				<Box mb={3}>
					<ParagraphSmall color="slateBlue">
						{t('common.settings.momentTemplates.edit.instruction')}
					</ParagraphSmall>
				</Box>
				<MultilingualQuill name={`${name}.instruction`} />
			</Box>

			{momentTemplate.type !== graphql.MomentType.Message && (
				<>
					<Label mt={5}>{t('common.settings.momentTemplates.edit.reminders.title')}</Label>
					<Box mb={3}>
						{notifications.value.map((notification, index) => (
							<Notification
								key={notification.id}
								name={`${name}.notifications[${index}]`}
								url={`${url}/notifications/${notification.id}`}
								momentDuration={`${name}.duration`}
								onRemove={() => {
									const updatedNotifications = notifications.value.slice();
									updatedNotifications.splice(index, 1);
									setNotificationsValue(updatedNotifications);
								}}
							/>
						))}
					</Box>
					<Box>
						<Button
							size={32}
							onClick={() => {
								const id = `_${Math.random().toString()}`;

								setNotificationsValue(
									notifications.value.concat([
										{
											id,
											type: graphql.MomentNotificationType.BeforeStart,
											offset: 0,
										},
									]),
								);

								return false;
							}}
						>
							{t('common.settings.momentTemplates.edit.addNotification')}
						</Button>
					</Box>
					<Hint>
						<HintIcon>{t('common.hint.title')}</HintIcon>
						<HintMessage>{t('common.settings.momentTemplates.hint.notifications')}</HintMessage>
					</Hint>
				</>
			)}
			<Flex justifyContent="flex-end" mt={3}>
				<Button size={ButtonSize.Medium} onClick={() => history.push(returnURL)}>
					{t('common.done')}
				</Button>
			</Flex>
		</Flex>
	);
}
